import { ReactComponent as DownIcon } from 'assets/icon/chevron-down.svg';
import { ReactComponent as CrossIcon } from 'assets/icon/x.svg';
import { capitalize, formatText } from 'helpers/utils';
import { useTranslation } from 'react-i18next';
import { useState, useRef, useEffect } from 'react';

export interface IDefaultOption {
  id: string;
  name: string;
  value?: string;
  _id?: string;
}

interface ISelectProps<T> {
  placeholder: string;
  selected: T | null;
  setSelected: (selectedValue: T) => void;
  options: T[];
  isSearchable?: boolean;
  search?: string;
  setSearch?: (data: string) => void;
  removeSearchOnBlur?: boolean;
  disabled?: boolean;
  error?: string;
  dropdownPlacement?: 'TOP' | 'BOTTOM';
  showCross?: boolean;
  handleCross?: () => void;
  height?: string;
}

// Define an empty option
const emptyOption: IDefaultOption = { id: '', name: '' };

const Select = <T extends IDefaultOption>({
  placeholder = '',
  selected = emptyOption as T,
  setSelected = () => {},
  options = [],
  isSearchable = false,
  search = '',
  setSearch = () => {},
  removeSearchOnBlur = false,
  disabled = false,
  error = '',
  dropdownPlacement = 'BOTTOM',
  showCross = true,
  handleCross = () => {},
  height = 'h-12',
}: ISelectProps<T>) => {
  const [show, setShow] = useState<boolean>(false);
  const [edit, setEdit] = useState<boolean>(false);
  const [dynamicPlacement, setDynamicPlacement] = useState<'TOP' | 'BOTTOM'>(dropdownPlacement);
  const selectRef = useRef<HTMLDivElement>(null);

  const { t } = useTranslation();

  // Fixed: Use mousedown/click events instead of focus/blur for better control
  const handleContainerClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (disabled) return;
    
    if (isSearchable) {
      setEdit(true);
      setShow(true);
    } else {
      setShow(!show);
    }
  };

  // Handle clicks outside the component
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (selectRef.current && !selectRef.current.contains(event.target as Node)) {
        setShow(false);
        setEdit(false);
        if (removeSearchOnBlur) {
          setSearch('');
        }
      }
    };

    if (show) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [show, removeSearchOnBlur, setSearch]);

  const calculateDropdownPlacement = () => {
    if (selectRef.current && dropdownPlacement === 'BOTTOM') {
      const rect = selectRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const spaceBelow = viewportHeight - rect.bottom;
      const spaceAbove = rect.top;

      if (spaceBelow < 200 && spaceAbove > spaceBelow) {
        setDynamicPlacement('TOP');
      } else {
        setDynamicPlacement('BOTTOM');
      }
    }
  };

  useEffect(() => {
    if (show) {
      calculateDropdownPlacement();
    }
  }, [show]);

  const onselect = (option: T) => {
    setEdit(false);
    setShow(false);
    setSelected(option);
  };

  const handleCancel = (e: React.MouseEvent) => {
    e.stopPropagation();
    setEdit(false);
    setShow(false);
    setSelected(emptyOption as T);
    handleCross();
  };

  // Handle input focus for searchable dropdowns
  const handleInputFocus = () => {
    if (selected) {
      setSearch(selected.name);
    }
  };

  return (
    <div className="flex flex-col relative w-full h-fit" ref={selectRef}>
      <div
        className={`flex items-center relative rounded-1 border-1.5 w-full px-4 h-10 ${`lg:${height}`} hover:border-active cursor-pointer ${
          show ? 'border-active' : 'border-grey'
        } ${error ? 'border-red-600' : ''} ${disabled ? ' bg-grey1 opacity-50' : 'bg-white'}`}
        onClick={handleContainerClick}
      >
        <div className="flex flex-1 items-center w-[calc(100%-24px)]">
          {isSearchable && edit ? (
            <input
              className="w-full border-0 text-base font-medium outline-none text-black2"
              autoFocus
              value={search}
              onChange={e => setSearch(e.target.value)}
              onFocus={handleInputFocus}
              onClick={e => e.stopPropagation()}
            />
          ) : selected && selected.name !== '' ? (
            <label className="text-base font-medium text-black2 whitespace-nowrap overflow-hidden text-ellipsis w-full">
              {selected?.name?.includes('_')
                ? capitalize(formatText(selected?.name, ' '))
                : selected?.name}
            </label>
          ) : (
            <label className="text-sm font-regular text-grey2 whitespace-nowrap overflow-hidden text-ellipsis w-full">
              {placeholder}
            </label>
          )}
        </div>
        <div className="flex items-center">
          {showCross && selected && !disabled && (
            <div className="flex items-center justify-center border-r-1 pr-1">
              <CrossIcon
                height={20}
                width={20}
                className="flex items-center justify-center"
                onClick={handleCancel}
              />
            </div>
          )}
          <DownIcon
            height={20}
            width={20}
            className={`flex items-center justify-center pl-1 ${show ? 'rotate-180' : ''}`}
          />
        </div>
      </div>
      {error && <p className="font-regular text-xs text-red-500">{error}</p>}
      <div
        className={`flex flex-col absolute w-full overscroll-y-auto max-h-36 rounded-3 box-border z-10 bg-[#FAF9F6] shadow-md ${
          show ? 'flex' : 'hidden'
        } ${dynamicPlacement === 'TOP' ? 'top-[unset] bottom-[52px]' : 'top-[52px]'}`}
      >
        {options?.length ? (
          <div className="flex flex-col w-full py-2 px-2 overflow-y-scroll overflow-x-hidden scrollbar-thin">
            {options
              ?.filter(option => 
                !isSearchable || !search || option.name.toLowerCase().includes(search.toLowerCase())
              )
              ?.map(option => (
                <div
                  className="flex items-center rounded-1 px-2 hover:bg-primary hover:text-white text-grey2 py-1 cursor-pointer"
                  key={option.id}
                  onClick={() => onselect(option)}
                >
                  <label className="text-base whitespace-nowrap cursor-pointer">
                    {option?.name?.includes('_')
                      ? capitalize(formatText(option?.name, ' '))
                      : option?.name}
                  </label>
                </div>
              ))}
          </div>
        ) : (
          <div className="flex items-center justify-center w-full py-4">
            <label className="font-medium text-grey2 text-base m-auto">{t('NO_OPTION')}</label>
          </div>
        )}
      </div>
    </div>
  );
};

export default Select;